import { cleanString, numberOrNull, flattenGrouped } from './helpers';
import { SERVICE_GROUPS } from '../../pages/accommodation/services/ServicesSection';

/* ============================================================
   SERVICES - FRONTEND -> BACKEND
   ------------------------------------------------------------
   The Services step groups services by category:
   { food: [...], transport: [...], ... }. The backend only
   has ONE flat "services" array, so we flatten before mapping
   each item.

   IMPORTANT: this used to receive the grouped object directly
   and run it through Array.isArray()-based helpers, which
   silently produced an empty array every time - every service
   an organizer added on this step was dropped before it ever
   reached the backend. flattenGrouped() fixes that by handling
   both a flat array (already-backend-shaped data coming back
   through the reverse mapper) and the grouped object the UI
   actually produces.
============================================================ */

export function transformServices(services = []) {
    return flattenGrouped(services)
        .map((service) => {
            const paid = service?.type === 'paid';

            // included / paid / on_request (stored, so it shows the same on edit)
            const type = ['included', 'paid', 'on_request'].includes(service?.type) ? service.type : undefined;

            return {
                name: cleanString(service?.name),

                description: cleanString(service?.description),

                type,

                // only paid services carry a price (an old price on an "included" one flipped it back to paid)
                price: !type || paid ? numberOrNull(service?.price) || 0 : 0,

                priceUnit: cleanString(service?.priceUnit || service?.unit) || 'per_booking',

                optional: service?.optional !== false,

                active: service?.active !== false
            };
        })
        .filter((service) => service.name);
}

/* ============================================================
   SERVICES - BACKEND -> FRONTEND
   ------------------------------------------------------------
   Backend gives back one flat array. The Services step wants
   it grouped back into { food: [...], transport: [...], ... }
   so the preset toggle buttons and per-item config rows show
   the right state. Every preset group's item list (from
   ServicesSection.jsx) is used to match each service back to
   its group by name.

   A service whose name doesn't match any preset item (i.e. it
   was added through "+ Add Custom Service") is kept in a
   "custom" bucket. ServicesSection doesn't render a group for
   "custom" today, so those items won't show as toggled presets,
   but the data is preserved on formData.services and will be
   sent back unchanged if the organizer resubmits without
   touching this step - it is not lost, just not editable
   in the UI yet.
============================================================ */

function buildNameToGroupIndex() {
    const index = new Map();

    SERVICE_GROUPS.forEach((group) => {
        group.items.forEach((item) => {
            index.set(item.toLowerCase(), group.key);
        });
    });

    return index;
}

const NAME_TO_GROUP = buildNameToGroupIndex();

function toFrontendServiceItem(service) {
    /*
     * NOTE: the backend service schema has no "type" field at
     * all (included / paid / on_request) - the forward
     * transform never sends it, only price does. So the best
     * we can do on the way back is infer "paid" from a
     * positive price; "on_request" can never be reconstructed
     * and will always come back as "included" with price 0.
     * Fixing this properly needs a "type" column added to the
     * backend service schema.
     */
    const price = numberOrNull(service?.price) || 0;
    const savedType = ['included', 'paid', 'on_request'].includes(service?.type) ? service.type : null;

    return {
        name: cleanString(service?.name),
        // saved type; older records: paid when there's a price
        type: savedType || (price > 0 ? 'paid' : 'included'),
        optional: service?.optional !== false,
        price,
        currency: 'INR',
        unit: cleanString(service?.priceUnit) || 'per_booking',
        description: cleanString(service?.description),
        active: service?.active !== false
    };
}

export function reverseServices(services = []) {
    const grouped = {
        food: [],
        transport: [],
        activities: [],
        wellness: [],
        housekeeping: [],
        business: [],
        custom: []
    };

    flattenGrouped(services).forEach((service) => {
        const name = cleanString(service?.name);

        if (!name) return;

        const groupKey = NAME_TO_GROUP.get(name.toLowerCase()) || 'custom';

        grouped[groupKey].push(toFrontendServiceItem(service));
    });

    return grouped;
}
